import { Modal as AmelioModal, type OptionProps } from '@amelio/ui-library';
import { useMemo, useState } from 'react';

import { CascadeCompetencyModal } from './components/CascadeCompetencyModal';
import { Competencies } from './components/Competencies';
import { CompetencyForm } from './components/CompetencyForm';
import { CompetencyGroupForm } from './components/CompetencyGroupForm';
import { DeactivateGroupModal } from './components/DeactivateGroupModal';
import { DeleteGroupModal } from './components/DeleteGroupModal';
import { ExploreCompetenciesModal } from './components/ExploreCompetenciesModal';
import { MoveCompetencyModal } from './components/MoveCompetencyModal';
import { isNameConflictError, wouldNameConflictInGroup } from './validation';

import type { CompetencyFormData, CompetencyGroupFormData } from './validation';
import type { Competency, CompetencyGroup } from '@/types/competency/Competency';

import {
    useChangeCompetencyGroupStatus,
    useChangeCompetencyStatus,
    useCreateCompetency,
    useCreateCompetencyGroup,
    useDeactivateCompetencyGroup,
    useGetCompetencyGroups,
    useMoveCompetency,
    useRemoveCompetencyGroup,
    useUpdateCompetency,
    useUpdateCompetencyGroup,
} from '@/api/competency-services/competency';
import { useAmelioTranslations } from '@/hooks';
import { useFetchErrorsHandler } from '@/hooks/api/useFetchErrorsHandler';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { useError } from '@/hooks/utils/useError';
import { showErrorToast, showSuccessToast } from '@/plugins/Toast';
import { CompetencyStatus } from '@/types/competency/Competency';
import { getTextByLanguage, stripEmptyLocalizedValues } from '@/utils/localization';

type GroupFormState = { mode: 'create' | 'edit'; group?: CompetencyGroup };
type CompetencyFormState = { mode: 'create' | 'edit'; competency?: Competency; groupId?: string };

/**
 * Builds a localized record by setting the value only for the active language.
 * No base language is auto-filled; each value is stored under the active UI language.
 */
const buildLocalizedRecord = (
    existing: Record<string, string> | undefined,
    language: string,
    value: string,
): Record<string, string> =>
    stripEmptyLocalizedValues({
        ...(existing ?? {}),
        [language]: value,
    });

export const CompetenciesPage = () => {
    const { t } = useAmelioTranslations();
    const language = useSelectedAppLanguage();
    const { handleError } = useError();

    const {
        data: competencyGroups,
        isLoading: isLoadingCompetencyGroups,
        isError: isCompetencyGroupsError,
        refetch: refetchCompetencyGroups,
    } = useGetCompetencyGroups();

    const { mutateAsync: createGroup, isPending: isCreatingGroup } = useCreateCompetencyGroup();
    const { mutateAsync: updateGroup, isPending: isUpdatingGroup } = useUpdateCompetencyGroup();
    const { mutateAsync: removeGroup, isPending: isRemovingGroup } = useRemoveCompetencyGroup();
    const { mutateAsync: deactivateGroup, isPending: isDeactivatingGroup } =
        useDeactivateCompetencyGroup();
    const { mutateAsync: createCompetency, isPending: isCreatingCompetency } =
        useCreateCompetency();
    const { mutateAsync: updateCompetency, isPending: isUpdatingCompetency } =
        useUpdateCompetency();
    const { mutateAsync: moveCompetency, isPending: isMovingCompetency } = useMoveCompetency();
    const { mutateAsync: changeCompetencyStatus } = useChangeCompetencyStatus();
    const { mutateAsync: changeCompetencyGroupStatus } = useChangeCompetencyGroupStatus();

    const [groupFormState, setGroupFormState] = useState<GroupFormState | null>(null);
    const [competencyFormState, setCompetencyFormState] = useState<CompetencyFormState | null>(
        null,
    );
    const [groupToDelete, setGroupToDelete] = useState<CompetencyGroup | null>(null);
    const [groupToDeactivate, setGroupToDeactivate] = useState<CompetencyGroup | null>(null);
    const [competencyToDelete, setCompetencyToDelete] = useState<Competency | null>(null);
    const [competencyToDeactivate, setCompetencyToDeactivate] = useState<Competency | null>(null);
    const [competencyToMove, setCompetencyToMove] = useState<Competency | null>(null);
    const [isExploreOpen, setIsExploreOpen] = useState(false);

    const groups = useMemo(() => competencyGroups ?? [], [competencyGroups]);

    useFetchErrorsHandler({
        errors: [isCompetencyGroupsError],
    });

    const groupOptions = useMemo<OptionProps[]>(
        () =>
            groups
                .map((group) => ({
                    id: group.id,
                    label: getTextByLanguage(group.name, language),
                    value: group.id,
                }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        [groups, language],
    );

    const activeCompetencyOptions = useMemo<OptionProps[]>(
        () =>
            groups
                .flatMap((group) => group.competencies)
                .filter((competency) => competency.status === CompetencyStatus.ACTIVE)
                .map((competency) => ({
                    id: competency.id,
                    label: getTextByLanguage(competency.name, language),
                    value: competency.id,
                })),
        [groups, language],
    );

    const deleteGroupReplacementOptions = useMemo<OptionProps[]>(
        () =>
            activeCompetencyOptions.filter(
                (option) =>
                    !groupToDelete?.competencies.some((competency) => competency.id === option.id),
            ),
        [activeCompetencyOptions, groupToDelete],
    );

    const deactivateGroupReplacementOptions = useMemo<OptionProps[]>(
        () =>
            activeCompetencyOptions.filter(
                (option) =>
                    !groupToDeactivate?.competencies.some(
                        (competency) => competency.id === option.id,
                    ),
            ),
        [activeCompetencyOptions, groupToDeactivate],
    );

    const nextGroupSortOrder = groups.length;

    const onSubmitGroup = async (data: CompetencyGroupFormData) => {
        try {
            if (groupFormState?.mode === 'edit' && groupFormState.group) {
                const { group: formGroup } = groupFormState;
                const group = groups.find((item) => item.id === formGroup.id) ?? formGroup;

                await updateGroup({
                    id: group.id,
                    payload: {
                        name: buildLocalizedRecord(group.name, language, data.name),
                        description: buildLocalizedRecord(
                            group.description,
                            language,
                            data.description,
                        ),
                        sortOrder: group.sortOrder,
                        status: group.status,
                    },
                });

                showSuccessToast({ message: t('competencies.toast.groupUpdated') });
            } else {
                await createGroup({
                    name: buildLocalizedRecord(undefined, language, data.name),
                    description: buildLocalizedRecord(undefined, language, data.description),
                    sortOrder: nextGroupSortOrder,
                    status: CompetencyStatus.ACTIVE,
                });

                showSuccessToast({ message: t('competencies.toast.groupCreated') });
            }

            setGroupFormState(null);
        } catch (error) {
            if (isNameConflictError(error)) {
                throw error;
            }

            handleError({ error });
        }
    };

    const onSubmitCompetency = async (data: CompetencyFormData) => {
        try {
            if (competencyFormState?.mode === 'edit' && competencyFormState.competency) {
                const { competency: formCompetency } = competencyFormState;
                const competency =
                    groups
                        .flatMap((group) => group.competencies)
                        .find((item) => item.id === formCompetency.id) ?? formCompetency;

                await updateCompetency({
                    id: competency.id,
                    payload: {
                        name: buildLocalizedRecord(competency.name, language, data.name),
                        description: buildLocalizedRecord(
                            competency.description,
                            language,
                            data.description,
                        ),
                        sortOrder: competency.sortOrder,
                        status: competency.status,
                    },
                });

                if (
                    competency.competencyGroupId &&
                    competency.competencyGroupId !== data.competencyGroupId
                ) {
                    await moveCompetency({
                        id: competency.id,
                        targetCompetencyGroupId: data.competencyGroupId,
                    });
                }

                showSuccessToast({ message: t('competencies.toast.competencyUpdated') });
            } else {
                const targetGroup = groups.find((group) => group.id === data.competencyGroupId);

                await createCompetency({
                    competencyGroupId: data.competencyGroupId,
                    name: buildLocalizedRecord(undefined, language, data.name),
                    description: buildLocalizedRecord(undefined, language, data.description),
                    sortOrder: targetGroup?.competencies.length ?? 0,
                    status: targetGroup?.status ?? CompetencyStatus.ACTIVE,
                });

                showSuccessToast({ message: t('competencies.toast.competencyCreated') });
            }

            setCompetencyFormState(null);
        } catch (error) {
            if (isNameConflictError(error)) {
                throw error;
            }

            handleError({ error });
        }
    };

    const onConfirmDeleteGroup = async (replacements: Record<string, string | undefined> = {}) => {
        if (!groupToDelete) {
            return;
        }

        try {
            await removeGroup({ id: groupToDelete.id, replacements });
            showSuccessToast({ message: t('competencies.toast.groupDeleted') });
            setGroupToDelete(null);
        } catch (error) {
            handleError({ error });
        }
    };

    const onConfirmMoveCompetency = async (targetCompetencyGroupId: string) => {
        if (!competencyToMove) {
            return;
        }

        const targetGroup = groups.find((group) => group.id === targetCompetencyGroupId);
        if (
            targetGroup &&
            wouldNameConflictInGroup(competencyToMove.name, targetGroup.competencies)
        ) {
            showErrorToast({ message: t('competencies.validation.duplicateNameInGroup') });

            return;
        }

        try {
            await moveCompetency({ id: competencyToMove.id, targetCompetencyGroupId });
            showSuccessToast({ message: t('competencies.toast.competencyMoved') });
            setCompetencyToMove(null);
        } catch (error) {
            if (isNameConflictError(error)) {
                showErrorToast({ message: t('competencies.validation.duplicateNameInGroup') });

                return;
            }

            handleError({ error });
        }
    };

    const onToggleGroupStatus = async (group: CompetencyGroup) => {
        if (group.status === CompetencyStatus.ACTIVE) {
            setGroupToDeactivate(group);

            return;
        }

        try {
            await changeCompetencyGroupStatus({ id: group.id, status: CompetencyStatus.ACTIVE });

            const remainingCount = group.competencies.filter(
                (competency) => competency.isIndividuallyDeactivated,
            ).length;
            const groupName = getTextByLanguage(group.name, language);

            showSuccessToast({
                message:
                    remainingCount > 0
                        ? t('competencies.toast.groupActivatedWithRemaining', {
                              name: groupName,
                              count: remainingCount,
                          })
                        : t('competencies.toast.groupActivated', { name: groupName }),
            });
        } catch (error) {
            handleError({ error });
        }
    };

    const onConfirmDeactivateGroup = async (
        replacements: Record<string, string | undefined> = {},
    ) => {
        if (!groupToDeactivate) {
            return;
        }

        try {
            await deactivateGroup({ id: groupToDeactivate.id, replacements });

            showSuccessToast({
                message: t('competencies.toast.groupDeactivated', {
                    name: getTextByLanguage(groupToDeactivate.name, language),
                }),
            });
            setGroupToDeactivate(null);
        } catch (error) {
            handleError({ error });
        }
    };

    const onToggleCompetencyStatus = async (competency: Competency) => {
        if (competency.status === CompetencyStatus.ACTIVE) {
            setCompetencyToDeactivate(competency);

            return;
        }

        try {
            await changeCompetencyStatus({ id: competency.id, status: CompetencyStatus.ACTIVE });
            showSuccessToast({ message: t('competencies.toast.competencyActivated') });
        } catch (error) {
            handleError({ error });
        }
    };

    return (
        <>
            <Competencies
                competencyGroups={groups}
                isLoading={isLoadingCompetencyGroups}
                isError={isCompetencyGroupsError}
                onRetry={() => void refetchCompetencyGroups()}
                onAddGroup={() => setGroupFormState({ mode: 'create' })}
                onAddCompetency={(groupId) => setCompetencyFormState({ mode: 'create', groupId })}
                onExplore={() => setIsExploreOpen(true)}
                onEditGroup={(group) => setGroupFormState({ mode: 'edit', group })}
                onToggleGroupStatus={onToggleGroupStatus}
                onDeleteGroup={(group) => setGroupToDelete(group)}
                onEditCompetency={(competency, group) =>
                    setCompetencyFormState({
                        mode: 'edit',
                        competency,
                        groupId: competency.competencyGroupId ?? group.id,
                    })
                }
                onMoveCompetency={(competency) => setCompetencyToMove(competency)}
                onToggleCompetencyStatus={onToggleCompetencyStatus}
                onDeleteCompetency={(competency) => setCompetencyToDelete(competency)}
            />

            <AmelioModal
                isOpen={Boolean(groupFormState)}
                onClose={() => setGroupFormState(null)}
                className={'w-[520px] max-w-[92vw]'}
                dataTestId={'competency-group-modal'}
                zIndex={50}
            >
                {groupFormState && (
                    <CompetencyGroupForm
                        mode={groupFormState.mode}
                        defaultValues={{
                            name: getTextByLanguage(groupFormState.group?.name, language),
                            description: getTextByLanguage(
                                groupFormState.group?.description,
                                language,
                            ),
                        }}
                        isSubmitting={isCreatingGroup || isUpdatingGroup}
                        onSubmit={onSubmitGroup}
                        onCancel={() => setGroupFormState(null)}
                    />
                )}
            </AmelioModal>

            <AmelioModal
                isOpen={Boolean(competencyFormState)}
                onClose={() => setCompetencyFormState(null)}
                className={'w-[520px] max-w-[92vw]'}
                dataTestId={'competency-modal'}
                zIndex={50}
            >
                {competencyFormState && (
                    <CompetencyForm
                        mode={competencyFormState.mode}
                        groupOptions={groupOptions}
                        defaultValues={{
                            name: getTextByLanguage(competencyFormState.competency?.name, language),
                            description: getTextByLanguage(
                                competencyFormState.competency?.description,
                                language,
                            ),
                            competencyGroupId:
                                competencyFormState.groupId ?? groupOptions[0]?.id ?? '',
                        }}
                        isSubmitting={isCreatingCompetency || isUpdatingCompetency}
                        onSubmit={onSubmitCompetency}
                        onCancel={() => setCompetencyFormState(null)}
                    />
                )}
            </AmelioModal>

            {groupToDelete && (
                <DeleteGroupModal
                    groupId={groupToDelete.id}
                    groupName={getTextByLanguage(groupToDelete.name, language)}
                    competencies={groupToDelete.competencies}
                    replacementOptions={deleteGroupReplacementOptions}
                    isSubmitting={isRemovingGroup}
                    onConfirm={onConfirmDeleteGroup}
                    onCancel={() => setGroupToDelete(null)}
                />
            )}

            {groupToDeactivate && (
                <DeactivateGroupModal
                    groupId={groupToDeactivate.id}
                    groupName={getTextByLanguage(groupToDeactivate.name, language)}
                    competencies={groupToDeactivate.competencies}
                    replacementOptions={deactivateGroupReplacementOptions}
                    isSubmitting={isDeactivatingGroup}
                    onConfirm={onConfirmDeactivateGroup}
                    onCancel={() => setGroupToDeactivate(null)}
                />
            )}

            {competencyToMove && (
                <MoveCompetencyModal
                    competencyName={getTextByLanguage(competencyToMove.name, language)}
                    groupOptions={groupOptions.filter(
                        (option) => option.id !== competencyToMove.competencyGroupId,
                    )}
                    isSubmitting={isMovingCompetency}
                    onConfirm={onConfirmMoveCompetency}
                    onCancel={() => setCompetencyToMove(null)}
                />
            )}

            <ExploreCompetenciesModal
                isOpen={isExploreOpen}
                onClose={() => setIsExploreOpen(false)}
            />

            <AmelioModal
                isOpen={Boolean(competencyToDeactivate)}
                onClose={() => setCompetencyToDeactivate(null)}
                className={'w-168 max-w-[92vw]'}
                dataTestId={'competency-deactivate-modal'}
                zIndex={50}
            >
                {competencyToDeactivate && (
                    <CascadeCompetencyModal
                        mode={'deactivate'}
                        competencyId={competencyToDeactivate.id}
                        competencyName={getTextByLanguage(competencyToDeactivate.name, language)}
                        onClose={() => setCompetencyToDeactivate(null)}
                    />
                )}
            </AmelioModal>

            <AmelioModal
                isOpen={Boolean(competencyToDelete)}
                onClose={() => setCompetencyToDelete(null)}
                className={'w-168 max-w-[92vw]'}
                dataTestId={'competency-delete-modal'}
                zIndex={50}
            >
                {competencyToDelete && (
                    <CascadeCompetencyModal
                        mode={'delete'}
                        competencyId={competencyToDelete.id}
                        competencyName={getTextByLanguage(competencyToDelete.name, language)}
                        onClose={() => setCompetencyToDelete(null)}
                    />
                )}
            </AmelioModal>
        </>
    );
};
